/**
 * Reminders API - Backend Integration with localStorage fallback
 * Connects to /api/reminders endpoints when backend is available
 */

import notificationService from '../services/NotificationService';

// Types (JSDoc for better DX)
/**
 * @typedef {"push"|"email"|"both"} Channel
 * @typedef {"once"|"daily"|"weekly"|"monthly"|"custom"} Frequency
 *
 * @typedef {Object} Reminder
 * @property {string|number} id
 * @property {string} title
 * @property {string=} icon
 * @property {boolean} active
 * @property {Channel} channel
 * @property {Frequency} frequency
 * @property {string} nextAt // ISO
 * @property {string=} snoozedUntil // ISO
 * @property {string=} relatedTag // e.g. "#kochen"
 * @property {number=} relatedBookmarkCount
 * @property {string=} bookmarkId // linked bookmark ID
 * @property {string=} bookmarkUrl // linked bookmark URL
 * @property {string=} bookmarkTitle // linked bookmark title
 */

const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
const STORAGE_KEY = 'myboki_reminders';

// Helpers
const now = () => new Date();
const toISO = (d) => new Date(d).toISOString();
const addMinutes = (date, minutes) => new Date(date.getTime() + minutes * 60000);

/** Sort by next due occurrence (snoozedUntil first if present, else nextAt) */
export const sortByNextDue = (a, b) => {
  const an = a.snoozedUntil ? new Date(a.snoozedUntil) : new Date(a.nextAt);
  const bn = b.snoozedUntil ? new Date(b.snoozedUntil) : new Date(b.nextAt);
  return an.getTime() - bn.getTime();
};

// Get auth token
function getAuthToken() {
  return localStorage.getItem('authToken');
}

// API headers
function getHeaders() {
  const headers = {
    'Content-Type': 'application/json',
  };
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// Check if backend is available
let backendAvailable = null;
async function checkBackendAvailable() {
  if (backendAvailable !== null) return backendAvailable;

  if (!baseUrl) {
    backendAvailable = false;
    return false;
  }

  try {
    const response = await fetch(`${baseUrl}/health`, {
      method: 'GET',
      headers: getHeaders(),
    });
    backendAvailable = response.ok;
  } catch {
    backendAvailable = false;
  }
  return backendAvailable;
}

// localStorage fallback functions
function getLocalReminders() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveLocalReminders(reminders) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reminders));
  } catch (e) {
    console.error('Failed to save reminders to localStorage:', e);
  }
}

// Query helpers for local storage
function applyFilters(list, { status = 'all', frequency = 'all', q = '' }) {
  let out = [...list];
  if (status !== 'all') {
    out = out.filter((r) => (status === 'active' ? r.active : !r.active));
  }
  if (frequency !== 'all') {
    out = out.filter((r) => r.frequency === frequency);
  }
  if (q) {
    const query = q.toLowerCase();
    out = out.filter((r) =>
      r.title.toLowerCase().includes(query) || (r.relatedTag || '').toLowerCase().includes(query)
    );
  }
  return out.sort(sortByNextDue);
}

export const remindersApi = {
  /** List reminders with optional filters */
  async list(params = { status: 'all', frequency: 'all', q: '' }) {
    const useBackend = await checkBackendAvailable();

    if (useBackend) {
      try {
        const queryParams = new URLSearchParams();
        if (params.status === 'active') queryParams.append('activeOnly', 'true');

        const response = await fetch(`${baseUrl}/reminders?${queryParams}`, {
          method: 'GET',
          headers: getHeaders(),
        });

        if (!response.ok) throw new Error('Failed to fetch reminders');

        let data = await response.json();

        // Apply client-side filters that backend might not support
        if (params.frequency !== 'all') {
          data = data.filter((r) => r.frequency === params.frequency);
        }
        if (params.q) {
          const query = params.q.toLowerCase();
          data = data.filter((r) =>
            r.title.toLowerCase().includes(query) || (r.relatedTag || '').toLowerCase().includes(query)
          );
        }

        return data.sort(sortByNextDue);
      } catch (error) {
        console.warn('Backend fetch failed, using localStorage:', error);
      }
    }

    // Fallback to localStorage
    const reminders = getLocalReminders();
    return applyFilters(reminders, params);
  },

  /** Create a new reminder */
  async create(payload) {
    const useBackend = await checkBackendAvailable();

    const reminderData = {
      title: payload.title || 'Neuer Reminder',
      icon: payload.icon || '🔔',
      active: payload.active ?? true,
      channel: payload.channel || 'push',
      frequency: payload.frequency || 'once',
      nextAt: payload.nextAt || toISO(addMinutes(now(), 60)),
      relatedTag: payload.relatedTag,
      bookmarkId: payload.bookmarkId,
      bookmarkUrl: payload.bookmarkUrl,
      bookmarkTitle: payload.bookmarkTitle,
    };

    if (useBackend) {
      try {
        const response = await fetch(`${baseUrl}/reminders`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(reminderData),
        });

        if (!response.ok) throw new Error('Failed to create reminder');

        const newReminder = await response.json();

        // Also schedule local notification as backup
        if (newReminder.active && (newReminder.channel === 'push' || newReminder.channel === 'both')) {
          notificationService.scheduleNotification(newReminder);
        }

        return newReminder;
      } catch (error) {
        console.warn('Backend create failed, using localStorage:', error);
      }
    }

    // Fallback to localStorage
    const reminders = getLocalReminders();
    const id = `r${Math.random().toString(36).slice(2, 9)}`;
    const newR = {
      id,
      ...reminderData,
      snoozedUntil: undefined,
      relatedBookmarkCount: payload.relatedBookmarkCount || 0,
    };
    reminders.push(newR);
    reminders.sort(sortByNextDue);
    saveLocalReminders(reminders);

    // Schedule push notification locally
    if (newR.active && (newR.channel === 'push' || newR.channel === 'both')) {
      notificationService.scheduleNotification(newR);
    }

    return JSON.parse(JSON.stringify(newR));
  },

  /** Update an existing reminder */
  async update(id, payload) {
    const useBackend = await checkBackendAvailable();

    if (useBackend) {
      try {
        const response = await fetch(`${baseUrl}/reminders/${id}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error('Failed to update reminder');
        return await response.json();
      } catch (error) {
        console.warn('Backend update failed, using localStorage:', error);
      }
    }

    // Fallback to localStorage
    const reminders = getLocalReminders();
    const idx = reminders.findIndex((r) => r.id === id || r.id === String(id));
    if (idx === -1) throw new Error('Reminder not found');
    reminders[idx] = { ...reminders[idx], ...payload };
    reminders.sort(sortByNextDue);
    saveLocalReminders(reminders);
    return JSON.parse(JSON.stringify(reminders[idx]));
  },

  /** Delete a reminder */
  async remove(id) {
    const useBackend = await checkBackendAvailable();

    // Clear any scheduled notification
    notificationService.clearScheduledNotification(id);

    if (useBackend) {
      try {
        const response = await fetch(`${baseUrl}/reminders/${id}`, {
          method: 'DELETE',
          headers: getHeaders(),
        });

        if (!response.ok && response.status !== 204) {
          throw new Error('Failed to delete reminder');
        }
        return { success: true };
      } catch (error) {
        console.warn('Backend delete failed, using localStorage:', error);
      }
    }

    // Fallback to localStorage
    let reminders = getLocalReminders();
    reminders = reminders.filter((r) => r.id !== id && r.id !== String(id));
    saveLocalReminders(reminders);
    return { success: true };
  },

  /** Snooze by minutes: sets snoozedUntil */
  async snooze(id, minutes) {
    const useBackend = await checkBackendAvailable();

    if (useBackend) {
      try {
        const response = await fetch(`${baseUrl}/reminders/${id}/snooze`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ minutes }),
        });

        if (!response.ok) throw new Error('Failed to snooze reminder');

        const updated = await response.json();

        // Reschedule local notification
        if (updated.active && (updated.channel === 'push' || updated.channel === 'both')) {
          notificationService.scheduleNotification(updated);
        }

        return updated;
      } catch (error) {
        console.warn('Backend snooze failed, using localStorage:', error);
      }
    }

    // Fallback to localStorage
    const reminders = getLocalReminders();
    const r = reminders.find((x) => x.id === id || x.id === String(id));
    if (!r) throw new Error('Reminder not found');
    const until = toISO(addMinutes(now(), minutes));
    r.snoozedUntil = until;
    reminders.sort(sortByNextDue);
    saveLocalReminders(reminders);

    // Reschedule notification
    if (r.active && (r.channel === 'push' || r.channel === 'both')) {
      notificationService.scheduleNotification(r);
    }

    return JSON.parse(JSON.stringify(r));
  },

  /** Toggle active state */
  async toggle(id, active) {
    const useBackend = await checkBackendAvailable();

    if (useBackend) {
      try {
        const response = await fetch(`${baseUrl}/reminders/${id}/toggle`, {
          method: 'PATCH',
          headers: getHeaders(),
          body: JSON.stringify({ active }),
        });

        if (!response.ok) throw new Error('Failed to toggle reminder');

        const updated = await response.json();

        // Handle notification scheduling
        if (active && (updated.channel === 'push' || updated.channel === 'both')) {
          notificationService.scheduleNotification(updated);
        } else {
          notificationService.clearScheduledNotification(id);
        }

        return updated;
      } catch (error) {
        console.warn('Backend toggle failed, using localStorage:', error);
      }
    }

    // Fallback to localStorage
    const reminders = getLocalReminders();
    const r = reminders.find((x) => x.id === id || x.id === String(id));
    if (!r) throw new Error('Reminder not found');
    r.active = active;
    reminders.sort(sortByNextDue);
    saveLocalReminders(reminders);

    // Handle notification scheduling
    if (active && (r.channel === 'push' || r.channel === 'both')) {
      notificationService.scheduleNotification(r);
    } else {
      notificationService.clearScheduledNotification(id);
    }

    return JSON.parse(JSON.stringify(r));
  },

  /** Duplicate returns new reminder */
  async duplicate(id) {
    const useBackend = await checkBackendAvailable();

    if (useBackend) {
      try {
        const response = await fetch(`${baseUrl}/reminders/${id}/duplicate`, {
          method: 'POST',
          headers: getHeaders(),
        });

        if (!response.ok) throw new Error('Failed to duplicate reminder');
        return await response.json();
      } catch (error) {
        console.warn('Backend duplicate failed, using localStorage:', error);
      }
    }

    // Fallback to localStorage
    const reminders = getLocalReminders();
    const r = reminders.find((x) => x.id === id || x.id === String(id));
    if (!r) throw new Error('Reminder not found');
    const clone = {
      ...r,
      id: `r${Math.random().toString(36).slice(2, 9)}`,
      title: r.title + ' (Kopie)',
      snoozedUntil: undefined
    };
    reminders.push(clone);
    reminders.sort(sortByNextDue);
    saveLocalReminders(reminders);
    return JSON.parse(JSON.stringify(clone));
  },
};

export default remindersApi;
